/**
 * IEP / Services tab on student detail — meeting history, active services, therapy schedule, compliance.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import { Add, ExpandLess, ExpandMore } from '@mui/icons-material';
import { serviceMeetingAPI, classroomAPI, userManagementAPI, studentAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SERVICE_TYPES = [
  { value: 'SP', label: 'Speech (SP)' },
  { value: 'OT', label: 'Occupational Therapy (OT)' },
  { value: 'PT', label: 'Physical Therapy (PT)' },
  { value: 'SEIT', label: 'SEIT' },
  { value: 'SW', label: 'Social Work (SW)' },
  { value: 'PSY', label: 'Psychology (PSY)' },
  { value: 'VISION', label: 'Vision Services' },
  { value: 'HEARING', label: 'Hearing Services' },
  { value: 'OTHER', label: 'Other' },
];

const REVIEW_TYPES = [
  { value: 'annual', label: 'Annual Review' },
  { value: 'program', label: 'Program Review' },
  { value: 'supplemental', label: 'Supplemental Evaluation Review' },
];

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const emptyServiceRow = () => ({
  service_type: 'SP',
  frequency: '',
  frequency_unit: 'sessions_per_week',
  ratio: '1:1',
  duration_minutes: 30,
  notes: '',
});

const emptyMeetingForm = () => ({
  committee_type: 'cpse',
  review_type: 'annual',
  meeting_date: '',
  effective_date: '',
  chairperson: '',
  district: '',
  is_returning: true,
  class_size: '',
  full_day: null,
  aide_1to1: false,
  seit: false,
  resource_room: false,
  classroom: '',
  kindergarten_classification: '',
  kindergarten_placement: '',
  notes: '',
  approved_services: [emptyServiceRow()],
});

const formatDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.split('T')[0] : '';
  const [y, m, day] = s.split('-');
  return y && m && day ? `${Number(m)}/${Number(day)}/${y}` : '—';
};

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hour = Number(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m?.slice(0, 2) || '00'} ${ampm}`;
};

function ScheduleStatusChip({ status, slotsNeeded }) {
  if (status === 'scheduled') return <Chip label="Scheduled" size="small" color="success" />;
  if (status === 'partial') return <Chip label={`${slotsNeeded} slot(s) not scheduled`} size="small" color="warning" />;
  return <Chip label="Not scheduled" size="small" color="error" />;
}

export default function IepServicesTab({ studentId }) {
  const { user, currentSite } = useAuth();
  const canEdit = ['superadmin', 'admin', 'editor'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [activeServices, setActiveServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [meetingDetail, setMeetingDetail] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyMeetingForm());
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotForm, setSlotForm] = useState({
    service_type: 'SP',
    day_of_week: 0,
    start_time: '09:00',
    duration_minutes: 30,
    location: '',
    is_pullout: true,
    valid_from: new Date().toISOString().slice(0, 10),
    valid_until: '',
    status: 'active',
    notes: '',
  });
  const [committeeTypes, setCommitteeTypes] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [currentClassroom, setCurrentClassroom] = useState(null);
  const [slotSaving, setSlotSaving] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [assignTherapistOpen, setAssignTherapistOpen] = useState(null);
  const [assignTherapistId, setAssignTherapistId] = useState('');
  const [studentSessionId, setStudentSessionId] = useState(null);

  const loadTherapists = useCallback(async () => {
    try {
      const params = { role: 'therapist' };
      if (currentSite?.id) params.site = currentSite.id;
      const res = await userManagementAPI.getUsers(params);
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setTherapists(list);
    } catch {
      setTherapists([]);
    }
  }, [currentSite?.id]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meetingsRes, servicesRes, slotsRes, complianceRes, committeeRes] = await Promise.all([
        serviceMeetingAPI.listMeetings(studentId),
        serviceMeetingAPI.getActiveServices(studentId),
        serviceMeetingAPI.listSlots(studentId),
        serviceMeetingAPI.getCompliance(studentId),
        serviceMeetingAPI.getCommitteeTypes(studentId),
      ]);
      setMeetings(meetingsRes.data || []);
      setActiveServices(servicesRes.data?.services || []);
      setCurrentClassroom(servicesRes.data?.classroom || null);
      setSlots(slotsRes.data || []);
      setCompliance(complianceRes.data?.compliance || []);
      setCommitteeTypes(committeeRes.data?.committee_types || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load IEP data');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadAll();
    loadTherapists();
  }, [loadAll, loadTherapists]);

  useEffect(() => {
    studentAPI.getStudent(studentId).then((res) => {
      setStudentSessionId(res.data?.session || res.data?.session_id || null);
    }).catch(() => {});
  }, [studentId]);

  const loadClassroomsForSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await classroomAPI.list(sessionId);
      setClassrooms(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch {
      setClassrooms([]);
    }
  }, []);

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setMeetingDetail(null);
      return;
    }
    setExpandedId(id);
    try {
      const res = await serviceMeetingAPI.getMeeting(studentId, id);
      setMeetingDetail(res.data);
    } catch {
      setMeetingDetail(null);
    }
  };

  const openAddMeeting = () => {
    setEditId(null);
    setForm(emptyMeetingForm());
    loadClassroomsForSession(studentSessionId);
    setDialogOpen(true);
  };

  const openEditMeeting = async (id) => {
    try {
      const res = await serviceMeetingAPI.getMeeting(studentId, id);
      const m = res.data;
      setForm({
        ...m,
        approved_services: m.approved_services?.length ? m.approved_services : [emptyServiceRow()],
      });
      setEditId(id);
      loadClassroomsForSession(m.session || studentSessionId);
      setDialogOpen(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load meeting');
    }
  };

  const handleSaveMeeting = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        approved_services: form.approved_services.map((s) => ({
          ...s,
          frequency: s.frequency === '' ? null : Number(s.frequency),
          duration_minutes: Number(s.duration_minutes) || 30,
        })),
      };
      if (editId) {
        await serviceMeetingAPI.updateMeeting(studentId, editId, payload);
      } else {
        await serviceMeetingAPI.createMeeting(studentId, payload);
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateServiceRow = (idx, field, value) => {
    setForm((prev) => {
      const rows = [...prev.approved_services];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, approved_services: rows };
    });
  };

  const openSlotEdit = (slot) => {
    setEditingSlotId(slot.id);
    setSlotForm({
      service_type: slot.service_type,
      day_of_week: slot.day_of_week,
      start_time: slot.start_time?.slice(0, 5) || '09:00',
      duration_minutes: slot.duration_minutes,
      location: slot.location || '',
      is_pullout: slot.is_pullout,
      valid_from: slot.valid_from,
      valid_until: slot.valid_until || '',
      status: slot.status,
      therapist: slot.therapist || '',
      notes: slot.notes || '',
    });
    setSlotDialogOpen(true);
  };

  const openAddSlot = () => {
    setEditingSlotId(null);
    setSlotForm({
      service_type: activeServices[0]?.service_type || 'SP',
      day_of_week: 0,
      start_time: '09:00',
      duration_minutes: 30,
      location: '',
      is_pullout: true,
      valid_from: new Date().toISOString().slice(0, 10),
      valid_until: '',
      status: 'active',
      therapist: '',
      notes: '',
    });
    setSlotDialogOpen(true);
  };

  const handleSaveSlot = async () => {
    setSlotSaving(true);
    try {
      const payload = {
        student: studentId,
        ...slotForm,
        start_time: slotForm.start_time.length === 5 ? `${slotForm.start_time}:00` : slotForm.start_time,
        valid_until: slotForm.valid_until || null,
        therapist: slotForm.therapist || null,
      };
      if (editingSlotId) {
        await serviceMeetingAPI.updateSlot(editingSlotId, payload);
      } else {
        await serviceMeetingAPI.createSlot(payload);
      }
      setSlotDialogOpen(false);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSlotSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const slotsByDay = DAY_LABELS.map((_, dow) => slots.filter((s) => s.day_of_week === dow && s.status !== 'cancelled'));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Current classroom */}
      {currentClassroom && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Current classroom</Typography>
          <Typography variant="body1">
            {currentClassroom.label}
            {currentClassroom.teacher ? ` · ${currentClassroom.teacher}` : ''}
          </Typography>
        </Paper>
      )}

      {/* Active Services */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>Active Approved Services</Typography>
        {activeServices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No active IEP services for the current period.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Ratio</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Therapist</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeServices.map((s) => (
                  <TableRow key={s.service_type}>
                    <TableCell>{s.service_label}</TableCell>
                    <TableCell>{s.frequency_label || `${s.frequency}x/week`}</TableCell>
                    <TableCell>{s.ratio || '—'}</TableCell>
                    <TableCell>{s.duration_minutes} min</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Button size="small" onClick={() => {
                          setAssignTherapistOpen(s);
                          setAssignTherapistId(s.therapist_id ? String(s.therapist_id) : '');
                        }}>
                          {s.therapist_name || 'Assign'}
                        </Button>
                      ) : (
                        s.therapist_name || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <ScheduleStatusChip status={s.schedule_status} slotsNeeded={s.slots_needed} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Compliance */}
      {compliance.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>Session Compliance (this month)</Typography>
          {compliance.map((c) => (
            <Box key={c.service_type} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{c.service_label}</Typography>
                <Typography variant="body2" color={c.behind ? 'error.main' : 'text.secondary'}>
                  {c.delivered}/{c.mandated} delivered
                  {c.makeup_owed > 0 ? ` · ${c.makeup_owed} makeup owed` : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, c.pct_complete)}
                color={c.behind ? 'warning' : 'primary'}
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* Weekly Therapy Schedule */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" color="primary">Therapy Schedule</Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={openAddSlot}>
              Add slot
            </Button>
          )}
        </Box>
        <Grid container spacing={1}>
          {DAY_LABELS.map((day, dow) => (
            <Grid item xs={12} sm={6} md={2.4} key={day}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{day}</Typography>
              {slotsByDay[dow].length === 0 ? (
                <Typography variant="caption" color="text.secondary">—</Typography>
              ) : (
                slotsByDay[dow].map((slot) => (
                  <Box
                    key={slot.id}
                    onClick={() => canEdit && openSlotEdit(slot)}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: canEdit ? 'pointer' : 'default',
                      bgcolor: slot.approved_service ? 'action.hover' : 'background.paper',
                      '&:hover': canEdit ? { bgcolor: 'action.selected' } : {},
                    }}
                  >
                    <Typography variant="caption" display="block" fontWeight={600}>
                      {slot.service_label} · {formatTime(slot.start_time)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slot.therapist_name || 'No therapist'} · {slot.location || 'TBD'}
                    </Typography>
                  </Box>
                ))
              )}
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Meeting History */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" color="primary">CPSE / CSE Meeting History</Typography>
          {canEdit && (
            <Button size="small" variant="contained" startIcon={<Add />} onClick={openAddMeeting}>
              Add Meeting
            </Button>
          )}
        </Box>
        {meetings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No IEP meetings recorded.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Date</TableCell>
                  <TableCell>Review Type</TableCell>
                  <TableCell>Chairperson</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell>Services</TableCell>
                  {canEdit && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {meetings.map((m) => (
                  <React.Fragment key={m.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(m.id)}>
                          {expandedId === m.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatDate(m.meeting_date)}</TableCell>
                      <TableCell>{m.review_type_display || m.review_type}</TableCell>
                      <TableCell>{m.chairperson || '—'}</TableCell>
                      <TableCell>{m.district || '—'}</TableCell>
                      <TableCell>
                        {(m.service_summary || []).map((s) => (
                          <Chip key={s} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button size="small" onClick={() => openEditMeeting(m.id)}>Edit</Button>
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={canEdit ? 7 : 6} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expandedId === m.id}>
                          <Box sx={{ py: 2, px: 1 }}>
                            {meetingDetail?.id === m.id ? (
                              <>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Committee</Typography>
                                    <Typography variant="body2">{meetingDetail.committee_type_display || meetingDetail.committee_type}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Effective</Typography>
                                    <Typography variant="body2">{formatDate(meetingDetail.effective_date)}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Class placement</Typography>
                                    <Typography variant="body2">
                                      {meetingDetail.class_size || '—'}
                                      {meetingDetail.full_day != null ? (meetingDetail.full_day ? ' · Full day' : ' · Half day') : ''}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Classroom</Typography>
                                    <Typography variant="body2">{meetingDetail.classroom_label || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Discussion notes</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {meetingDetail.notes || '—'}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </>
                            ) : (
                              <CircularProgress size={20} />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Meeting Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit meeting record' : 'Add meeting record'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Committee</Typography>
              <RadioGroup row value={form.committee_type} onChange={(e) => setForm((p) => ({ ...p, committee_type: e.target.value }))}>
                {(committeeTypes.length ? committeeTypes : [{ value: 'cpse', label: 'CPSE' }]).map((c) => (
                  <FormControlLabel key={c.value} value={c.value} control={<Radio size="small" />} label={c.label} />
                ))}
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Review type</Typography>
              <RadioGroup row value={form.review_type} onChange={(e) => setForm((p) => ({ ...p, review_type: e.target.value }))}>
                {REVIEW_TYPES.map((r) => (
                  <FormControlLabel key={r.value} value={r.value} control={<Radio size="small" />} label={r.label} />
                ))}
              </RadioGroup>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Meeting date" type="date" InputLabelProps={{ shrink: true }}
                value={form.meeting_date} onChange={(e) => setForm((p) => ({ ...p, meeting_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Effective date" type="date" InputLabelProps={{ shrink: true }}
                value={form.effective_date} onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="District" value={form.district}
                onChange={(e) => setForm((p) => ({ ...p, district: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Chairperson" value={form.chairperson}
                onChange={(e) => setForm((p) => ({ ...p, chairperson: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Checkbox checked={form.is_returning} onChange={(e) => setForm((p) => ({ ...p, is_returning: e.target.checked }))} />}
                label="Returning student" />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="primary">Class placement (this session)</Typography></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Class size" value={form.class_size}
                onChange={(e) => setForm((p) => ({ ...p, class_size: e.target.value }))} placeholder="8:1:2" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Day length</InputLabel>
                <Select label="Day length" value={form.full_day === null ? '' : form.full_day ? 'full' : 'half'}
                  onChange={(e) => setForm((p) => ({ ...p, full_day: e.target.value === '' ? null : e.target.value === 'full' }))}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="full">Full day</MenuItem>
                  <MenuItem value="half">Half day</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Checkbox checked={form.aide_1to1} onChange={(e) => setForm((p) => ({ ...p, aide_1to1: e.target.checked }))} />} label="1:1 Aide" />
              <FormControlLabel control={<Checkbox checked={form.seit} onChange={(e) => setForm((p) => ({ ...p, seit: e.target.checked }))} />} label="SEIT" />
              <FormControlLabel control={<Checkbox checked={form.resource_room} onChange={(e) => setForm((p) => ({ ...p, resource_room: e.target.checked }))} />} label="Resource Room" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Classroom</InputLabel>
                <Select label="Classroom" value={form.classroom || ''} onChange={(e) => setForm((p) => ({ ...p, classroom: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {classrooms.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.label || c.name || c.class_num}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Related services</Typography>
              {form.approved_services.map((row, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Service</InputLabel>
                      <Select label="Service" value={row.service_type} onChange={(e) => updateServiceRow(idx, 'service_type', e.target.value)}>
                        {SERVICE_TYPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField fullWidth size="small" label="Frequency" type="number" value={row.frequency}
                      onChange={(e) => updateServiceRow(idx, 'frequency', e.target.value)} />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit</InputLabel>
                      <Select label="Unit" value={row.frequency_unit} onChange={(e) => updateServiceRow(idx, 'frequency_unit', e.target.value)}>
                        <MenuItem value="sessions_per_week">Sessions/week</MenuItem>
                        <MenuItem value="hours_per_month">Hours/month (EI)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField fullWidth size="small" label="Ratio" value={row.ratio}
                      onChange={(e) => updateServiceRow(idx, 'ratio', e.target.value)} />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <Button size="small" color="error" onClick={() => setForm((p) => ({ ...p, approved_services: p.approved_services.filter((_, i) => i !== idx) }))}
                      disabled={form.approved_services.length <= 1}>×</Button>
                  </Grid>
                </Grid>
              ))}
              <Button size="small" onClick={() => setForm((p) => ({ ...p, approved_services: [...p.approved_services, emptyServiceRow()] }))}>
                Add service row
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={4} label="Discussion notes, follow-up items, parent feedback"
                value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !saving && setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMeeting} disabled={saving || !form.meeting_date || !form.effective_date}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slot edit dialog */}
      <Dialog open={slotDialogOpen} onClose={() => !slotSaving && setSlotDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSlotId ? 'Edit therapy slot' : 'Add therapy slot'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select label="Service" value={slotForm.service_type} onChange={(e) => setSlotForm((p) => ({ ...p, service_type: e.target.value }))}>
                  {SERVICE_TYPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Day</InputLabel>
                <Select label="Day" value={slotForm.day_of_week} onChange={(e) => setSlotForm((p) => ({ ...p, day_of_week: e.target.value }))}>
                  {DAY_LABELS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Start time" type="time" InputLabelProps={{ shrink: true }}
                value={slotForm.start_time} onChange={(e) => setSlotForm((p) => ({ ...p, start_time: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Duration (min)" type="number" value={slotForm.duration_minutes}
                onChange={(e) => setSlotForm((p) => ({ ...p, duration_minutes: Number(e.target.value) }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Location" value={slotForm.location}
                onChange={(e) => setSlotForm((p) => ({ ...p, location: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Valid from" type="date" InputLabelProps={{ shrink: true }}
                value={slotForm.valid_from} onChange={(e) => setSlotForm((p) => ({ ...p, valid_from: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Valid until" type="date" InputLabelProps={{ shrink: true }}
                value={slotForm.valid_until} onChange={(e) => setSlotForm((p) => ({ ...p, valid_until: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Therapist</InputLabel>
                <Select label="Therapist" value={slotForm.therapist || ''} onChange={(e) => setSlotForm((p) => ({ ...p, therapist: e.target.value }))}>
                  <MenuItem value="">— Unassigned —</MenuItem>
                  {therapists.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Checkbox checked={slotForm.is_pullout} onChange={(e) => setSlotForm((p) => ({ ...p, is_pullout: e.target.checked }))} />}
                label="Pullout (vs push-in)" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !slotSaving && setSlotDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSlot} disabled={slotSaving}>{slotSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!assignTherapistOpen} onClose={() => setAssignTherapistOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign therapist — {assignTherapistOpen?.service_label}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Therapist</InputLabel>
            <Select label="Therapist" value={assignTherapistId} onChange={(e) => setAssignTherapistId(e.target.value)}>
              <MenuItem value="">— Select —</MenuItem>
              {therapists.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTherapistOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={!assignTherapistId} onClick={async () => {
            if (!assignTherapistOpen || !assignTherapistId) return;
            await serviceMeetingAPI.assignTherapist(studentId, assignTherapistOpen.approved_service_id, Number(assignTherapistId));
            setAssignTherapistOpen(null);
            loadAll();
          }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
